import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Box,
  Autocomplete,
  TextField,
  Chip,
  alpha,
  Alert,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

/**
 * Dialog para transferir todos los pacientes de un PCP a otro.
 */
export default function TransferDialog({ open, onClose, sourcePcp, pcps, locationId, onTransfer }) {
  const [targetPcp, setTargetPcp] = useState(null);
  const [loading, setLoading] = useState(false);

  const availablePcps = (pcps || []).filter(
    (p) => p.employeePcpId !== sourcePcp?.employeePcpId
  );

  const handleTransfer = async () => {
    if (!targetPcp || !sourcePcp) return;
    setLoading(true);
    try {
      await onTransfer({
        fromEmployeePcpId: sourcePcp.employeePcpId,
        fromLocationId: locationId,
        toEmployeePcpId: targetPcp.employeePcpId,
        toLocationId: locationId,
      });
      setTargetPcp(null);
      onClose();
    } catch {
      // Error handled by parent
    } finally {
      setLoading(false);
    }
  };

  if (!sourcePcp) return null;

  const capacityWarning = targetPcp?.available !== null && targetPcp?.available !== undefined
    && targetPcp.available < sourcePcp.currentLoad;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)", px: 3, py: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <SwapHorizIcon sx={{ color: "white" }} />
          <Typography variant="h6" fontWeight={700} sx={{ color: "white" }}>
            Transfer All Patients
          </Typography>
        </Stack>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Stack spacing={2.5}>
          {/* Source PCP info */}
          <Box sx={{ p: 2, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.divider, 0.08) }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary">FROM</Typography>
            <Typography variant="subtitle1" fontWeight={700}>
              {sourcePcp.title} {sourcePcp.pcpName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {sourcePcp.currentLoad} patients • {sourcePcp.centerName}
            </Typography>
          </Box>

          {/* Target PCP selector */}
          <Autocomplete
            value={targetPcp}
            onChange={(_, v) => setTargetPcp(v)}
            options={availablePcps}
            getOptionLabel={(o) => `${o.title} ${o.pcpName}`}
            renderOption={(props, option) => (
              <li {...props} key={option.employeePcpId}>
                <Stack>
                  <Typography variant="body2" fontWeight={600}>
                    {option.title} {option.pcpName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.currentLoad} patients • {option.available !== null ? `${option.available} available` : "No limit"} • {option.centerName}
                  </Typography>
                </Stack>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Select destination PCP" placeholder="Search PCP..." />
            )}
            size="small"
          />

          {/* Capacity warning */}
          {capacityWarning && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              Destination PCP may not have enough capacity. Available: {targetPcp.available}, Required: {sourcePcp.currentLoad}
            </Alert>
          )}

          {targetPcp && (
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04) }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary">TO</Typography>
              <Typography variant="subtitle1" fontWeight={700}>
                {targetPcp.title} {targetPcp.pcpName}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                <Chip label={`${targetPcp.currentLoad} current`} size="small" sx={{ height: 22, fontSize: "0.7rem" }} />
                <Chip
                  label={targetPcp.available !== null ? `${targetPcp.available} available` : "Unlimited"}
                  size="small"
                  sx={{ height: 22, fontSize: "0.7rem", bgcolor: "#D1FAE5", color: "#065F46" }}
                />
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2, textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleTransfer}
          variant="contained"
          disabled={!targetPcp || loading}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
          }}
        >
          {loading ? "Transferring..." : `Transfer ${sourcePcp.currentLoad} Patients`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
